import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useFormik } from "formik";
import { AxiosError } from "axios";
import { toast } from "sonner";
import {
  colorTokens,
  getSpacing,
  typographyTokens,
  getTypography,
} from "../../../../designTokens";
import {
  createPatientApi,
  patchPatientApi,
  type CreatePatientPayload,
} from "../../../../Apis/patients";
import { patchContactApi } from "../../../../Apis/contacts";
import {
  buildOptionalUpsertAddress,
  buildPatchPayloadFromFormDiff,
  buildPatientCrmPatchPayloadFromFormDiff,
  EMPTY_CONTACT_FORM_VALUES,
  formGenderToHisCode,
  formGenderToPatch,
  isPhoneSameAsRegistered,
  type ContactFormValues,
} from "../../../../utils/contactFormMappers";
import {
  addNewContactValidationSchema,
  editPatientCrmValidationSchema,
} from "../../../../validation/addNewContactSchema";
import { startFlow } from "../../../../analytics";
import type { EventMap } from "../../../../analytics";
import { fetchLocationByPincode } from "../../../../Apis/addressLookup";
import {
  getDistrictsForState,
  getPincodesForStateCity,
  loadIndianAddressData,
  toLocationSelectOptions,
  type IndianAddressData,
} from "../../../../utils/indianAddressLookup";
import { apiClient } from "../../../../Apis/auth";
import {
  getContactFormOptionsApi,
  type CampaignGroupingMapping,
} from "../../../../Apis/tenantConfig";
import {
  LEAD_SOURCE_OPTIONS,
  buildCreateFormLeadSourceOptions,
  getAutoSelectedChannelForSource,
  getOpportunityChannelOptionsForSource,
  matchLeadSourceCatalogOptionId,
  resolveLeadSourceForApi,
  sourceRequiresChannel,
  sourceShowsChannelField,
  displaySourceChannel,
} from "../../../../constants/leadSourceOptions";
import { getApiValidationErrorMessage } from "../../../../utils/apiValidationErrorMessage";
import CalenderIcon from "../../../../Assets/icons/CalenderIcon.svg";
import SearchIcon from "../../../../Assets/icons/Search.svg";
import checkboxCheckedIcon from "../../../../Assets/icons/clickedCheckbox.svg";
import checkboxIcon from "../../../../Assets/icons/CheckboxUnchecked.svg";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Switch,
} from "@mui/material";
import { SingleSelectDropdown, type SingleSelectOption } from "../../../common";
import DatePickerCalendar from "../../LeadDetailsPage/components/DatePickerCalendar";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { fetchFacilities } from "../../../../redux/slices/GlobalSlice";
import type { RootState } from "../../../../redux/store";

export type ContactFormMode = "create" | "edit";

interface AddNewContactModalProps {
  open: boolean;
  close: () => void;
  /** When set (e.g. from lead details "New Contact"), phone and display number are pre-filled with this value and both fields are disabled. */
  initialPhoneNumber?: string;
  /**
   * Editable create-mode prefill from CareFlow no-match search (CAR-2831).
   * Does not lock phone fields — unlike `initialPhoneNumber`.
   */
  searchPrefill?: {
    phoneNumber?: string;
    patientName?: string;
  };
  /** create = POST patient; edit = PATCH contact or patient (requires id + saveEnabled). */
  mode?: ContactFormMode;
  /** Contact ID for PATCH when mode is edit (contact lead) */
  contactId?: string;
  /** Patient ID for PATCH when mode is edit (patient lead, CAR-2844) */
  patientId?: string;
  /** Pre-fill form when editing an existing contact/patient */
  prefillValues?: Partial<ContactFormValues>;
  /** When false in edit mode, all fields are read-only */
  saveEnabled?: boolean;
  /** Called after contact or patient PATCH succeeds */
  onContactUpdated?: () => void;
}

/** Normalize phone/WhatsApp to digits only for API */
function toPhoneDigits(s: string): string {
  return (s ?? "").replace(/\D/g, "");
}

type FormValues = ContactFormValues;

/* Match AddUser component - fields and dropdown styling */
const textFieldSx = {
  marginBottom: getSpacing("4px"),

  "& .MuiOutlinedInput-root": {
    height: "48px",
    minHeight: "48px",
    maxHeight: "48px",
    boxSizing: "border-box",
    borderRadius: getSpacing("8px"),
    ...getTypography("Body/Medium"),
    backgroundColor: colorTokens["Background/White"],
    alignItems: "center",

    "& fieldset": {
      borderColor: colorTokens["Borders/Light"],
      borderWidth: "1px",
    },

    "&.Mui-focused fieldset": {
      borderColor: colorTokens["Primary/Default"],
      borderWidth: "1px",
    },

    "&.Mui-error fieldset": {
      borderColor: colorTokens["Alert/Red/Dark"],
      borderWidth: "1px",
    },

    "& input": {
      padding: `${getSpacing("12px")} ${getSpacing("16px")}`,
      boxSizing: "border-box",
      height: "100%",
    },

    /* Select dropdown - consistent height, no extra padding */
    "& .MuiSelect-select": {
      padding: `${getSpacing("12px")} ${getSpacing("40px")} ${getSpacing("12px")} ${getSpacing("16px")}`,
      boxSizing: "border-box",
      lineHeight: "1.4375em",
      minHeight: "unset",
    },

    /* Date input - keep calendar icon clickable */
    "& input[type='date']::-webkit-calendar-picker-indicator": {
      opacity: 0,
      position: "absolute",
      right: 0,
      width: "100%",
      height: "100%",
      cursor: "pointer",
    },
  },

  "& .MuiInputLabel-root": {
    ...getTypography("Body/Medium"),
    color: colorTokens["Text/Light Grey"],
    transform: "translate(14px, 14px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.857)",
    },
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: colorTokens["Primary/Default"],
  },

  "& .MuiInputLabel-root.Mui-error": {
    color: colorTokens["Alert/Red/Dark"],
  },

  "& .MuiInputLabel-root.Mui-error.Mui-focused": {
    color: colorTokens["Alert/Red/Dark"],
  },

  "& .MuiInputAdornment-root": {
    cursor: "pointer",
  },
};

/** Disabled styling for Display/WhatsApp when synced to phone — preserves field dimensions and border radius. */
const sameAsPhoneDisabledFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: "48px",
    minHeight: "48px",
    maxHeight: "48px",
    boxSizing: "border-box",
    borderRadius: getSpacing("8px"),
    ...getTypography("Body/Medium"),
    backgroundColor: colorTokens["Background/Greyish"],
    alignItems: "center",
    cursor: "not-allowed",
    "& fieldset": {
      borderColor: colorTokens["Borders/Light"],
      borderWidth: "1px",
    },
    "& input": {
      padding: `${getSpacing("12px")} ${getSpacing("16px")}`,
      boxSizing: "border-box",
      height: "100%",
      cursor: "not-allowed",
      WebkitTextFillColor: colorTokens["Text/Black"],
      opacity: 1,
    },
    "&.Mui-disabled": {
      backgroundColor: colorTokens["Background/Greyish"],
    },
  },
  "& .MuiInputLabel-root": {
    color: colorTokens["Text/Light Grey"],
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: colorTokens["Text/Light Grey"],
  },
};

const genderOptions = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const sourceOptions = LEAD_SOURCE_OPTIONS.map((opt) => ({
  value: opt.id,
  label: opt.label,
}));

const payerTypeOptions = [
  { value: "cash", label: "Cash" },
  { value: "insurance", label: "Insurance" },
  { value: "corporate", label: "Corporate" },
  { value: "panel", label: "Panel" },
];

const countryOptions = [
  { value: "India", label: "India" },
  { value: "Other", label: "Other" },
];

const DRAWER_WIDTH = "38.25rem";
const DRAWER_SLIDE_DURATION_MS = 300;
const ADDRESS_TO_PINCODE_DEBOUNCE_MS = 600;
const SKIP_AUTOFILL_MS = 1500;

function getDobForApi(
  dob: string | number | Date | null | undefined,
  age: string | number | null | undefined
): string {
  if (dob != null) {
    if (dob instanceof Date) {
      const iso = dob.toISOString().slice(0, 10);
      if (iso) return iso;
    }
    if (typeof dob === "number" || /^\d+$/.test(String(dob).trim())) {
      const dobStr = String(dob).trim();
      const len = dobStr.length;
      if (len > 4) {
        const ms =
          len === 10
            ? Number(dobStr) * 1000
            : typeof dob === "number"
              ? dob
              : Number(dobStr);
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) {
          const iso = d.toISOString().slice(0, 10);
          if (iso) return iso;
        }
      }
    }
    const dobStr = String(dob).trim();
    if (dobStr) {
      const d = new Date(dobStr);
      if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
  }
  const ageStr = age != null ? String(age) : "";
  const ageNum = parseInt(ageStr.trim() || "0", 10);
  if (ageNum > 0) {
    const year = new Date().getFullYear() - ageNum;
    return `${year}-01-01`;
  }
  return "";
}

function getAgeFromDob(dob: string): string {
  const trimmed = (dob ?? "").trim();
  if (!trimmed) return "";
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  if (d > today) return "";
  let age = today.getFullYear() - d.getFullYear();
  const monthDiff = today.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d.getDate())) {
    age -= 1;
  }
  return age >= 0 ? String(age) : "";
}

function getDobFromAge(age: string): string {
  const trimmed = (age ?? "").trim();
  if (!trimmed) return "";
  const ageNum = parseInt(trimmed, 10);
  if (Number.isNaN(ageNum) || ageNum < 0) return "";
  const year = new Date().getFullYear() - ageNum;
  return `${year}-01-01`;
}

/**
 * Pure builder for the `lead_created` terminal payload (US-B1 entry / US-D2
 * funnel completion). PHI-safe: emits the lead-source enum, campaign channel,
 * and opportunity-type enum only — never the patient name, phone, email, or
 * address held in the form.
 */
export function buildLeadCreatedProps(
  values: ContactFormValues
): EventMap["lead_created"] {
  const leadSource = (values.source ?? "Manual").trim() || "Manual";
  const sourceChannel = displaySourceChannel(leadSource, values.channel);
  const opportunityType = (values.opportunity ?? "").trim();
  return {
    created_via: "manual_ui",
    lead_source: leadSource,
    ...(sourceChannel ? { source_channel: sourceChannel } : {}),
    ...(opportunityType ? { opportunity_type: opportunityType } : {}),
    is_dedupe_match: false,
  };
}

/**
 * Phone fields are auto-managed — pre-filled from `initialPhoneNumber` and
 * synced via the "same as phone" toggles — so a change to them is NOT a signal
 * that the user has started entering lead details. Everything else counts.
 */
const AUTO_MANAGED_DETAIL_KEYS: ReadonlyArray<keyof ContactFormValues> = [
  "phoneNumber",
  "displayNumber",
  "whatsappNumber",
];

/**
 * True once the user has edited any content field relative to a per-open
 * baseline. Ignores the auto-managed phone fields so a programmatic prefill
 * (or a reopened modal whose form was never reset) does not count as a genuine
 * "details entered" for the US-D2 funnel.
 */
export function hasEnteredContactDetails(
  values: ContactFormValues,
  baseline: ContactFormValues
): boolean {
  return (Object.keys(values) as Array<keyof ContactFormValues>).some(
    (key) =>
      !AUTO_MANAGED_DETAIL_KEYS.includes(key) && values[key] !== baseline[key]
  );
}

const AddNewContactModal: React.FC<AddNewContactModalProps> = ({
  open,
  close,
  initialPhoneNumber,
  searchPrefill,
  mode = "create",
  contactId,
  patientId,
  prefillValues,
  saveEnabled = true,
  onContactUpdated,
}) => {
  const isEditMode = mode === "edit";
  /** Patient 360 edit: CRM-only fields writable; HIS-owned fields stay locked. */
  const isPatientEditMode = isEditMode && Boolean(patientId) && !contactId;
  const isReadOnly = isEditMode && !saveEnabled;
  /**
   * In patient edit mode, lock identity/HIS fields by treating the form as
   * mostly disabled; CRM fields below use `crmFieldsDisabled` instead.
   */
  const formFieldsDisabled = isReadOnly || isPatientEditMode;
  const crmFieldsDisabled = isReadOnly;
  const initialSnapshotRef = useRef<ContactFormValues | null>(null);
  const prevOpenRef = useRef(false);
  const flowRef = useRef<ReturnType<typeof startFlow> | null>(null);
  const detailsEnteredRef = useRef(false);
  const completedRef = useRef(false);
  const flowBaselineRef = useRef<ContactFormValues | null>(null);

  const mergedInitialValues = useMemo((): FormValues => {
    const base =
      isEditMode && prefillValues
        ? { ...EMPTY_CONTACT_FORM_VALUES, ...prefillValues }
        : { ...EMPTY_CONTACT_FORM_VALUES };

    if (!isEditMode && searchPrefill) {
      const name = searchPrefill.patientName?.trim();
      if (name) {
        base.patientName = name;
      }
      const phone = searchPrefill.phoneNumber?.trim();
      if (phone) {
        base.phoneNumber = phone;
        base.displayNumber = phone;
        base.whatsappNumber = phone;
      }
    }

    if (!base.channel?.trim()) {
      return {
        ...base,
        channel: getAutoSelectedChannelForSource(base.source ?? ""),
      };
    }
    return base;
  }, [isEditMode, prefillValues, searchPrefill]);
  interface AddressSearchResult {
    placeId: string;
    placeName: string;
    placeAddress: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
    latitude: string;
    longitude: string;
    types: string[];
  }

  const [sameAsPhone, setSameAsPhone] = React.useState(false);
  const [whatsappSameAsPhone, setWhatsappSameAsPhone] = React.useState(false);
  const phoneNumberLocked = Boolean(initialPhoneNumber?.trim());
  const [isDrawerVisible, setIsDrawerVisible] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [sourceOptionsFromApi, setSourceOptionsFromApi] = React.useState<
    SingleSelectOption[]
  >([]);
  const [addressResults, setAddressResults] = React.useState<
    AddressSearchResult[]
  >([]);
  const [, setIsAddressSearching] = React.useState(false);
  const [addressSearchError, setAddressSearchError] = React.useState<
    string | null
  >(null);
  const [addressSearchDisabledUntil, setAddressSearchDisabledUntil] =
    React.useState<number | null>(null);
  const [isSearchAddressFocused, setIsSearchAddressFocused] =
    React.useState(false);
  const [manualAddressEntry, setManualAddressEntry] = React.useState(false);
  const [indianAddressData, setIndianAddressData] =
    React.useState<IndianAddressData | null>(null);
  const [indianAddressLoading, setIndianAddressLoading] = React.useState(false);
  const [manualPincodeOptions, setManualPincodeOptions] = React.useState<
    SingleSelectOption[]
  >([]);
  const userEditedPincodeRef = React.useRef(false);
  const skipPincodeToAddressRef = React.useRef(false);
  const skipAddressToPincodeRef = React.useRef(false);
  const addressToPincodeTimerRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [isDobCalendarOpen, setIsDobCalendarOpen] = React.useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { facilities } = useAppSelector((state: RootState) => state.global);

  useEffect(() => {
    if (facilities.length === 0) dispatch(fetchFacilities());
  }, [dispatch, facilities.length]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const loadGroupings = async () => {
      try {
        const formOptionsRes = await getContactFormOptionsApi();

        if (cancelled) return;

        const sourceGrouping = (
          formOptionsRes.data?.campaignGroupings ?? []
        ).find(
          (
            grouping
          ): grouping is {
            props: { category?: string; mappings?: unknown[] };
          } => {
            if (
              typeof grouping !== "object" ||
              grouping === null ||
              !("props" in grouping)
            ) {
              return false;
            }
            const props = (grouping as { props?: unknown }).props;
            if (typeof props !== "object" || props === null) return false;
            const category = (props as { category?: unknown }).category;
            return category === "source";
          }
        );

        const mappings = (sourceGrouping?.props?.mappings ?? []).filter(
          (m): m is CampaignGroupingMapping => {
            if (typeof m !== "object" || m === null) return false;
            const rawValue = (m as { rawValue?: unknown }).rawValue;
            const groupName = (m as { groupName?: unknown }).groupName;
            const isActive = (m as { isActive?: unknown }).isActive;
            return (
              typeof rawValue === "string" &&
              typeof groupName === "string" &&
              !!rawValue.trim() &&
              !!groupName.trim() &&
              isActive !== false
            );
          }
        );

        const uniqueSourceMap = new Map<string, SingleSelectOption>();
        mappings.forEach((m) => {
          const sourceKey = m.groupName.trim();
          if (sourceKey && !uniqueSourceMap.has(sourceKey)) {
            uniqueSourceMap.set(sourceKey, {
              value: sourceKey,
              label: sourceKey,
            });
          }
        });

        const masterSources = (formOptionsRes.data?.leadSources ?? [])
          .filter(
            (source) => typeof source.code === "string" && source.code.trim()
          )
          .map((source) => ({
            id: source.code.trim(),
            label: (source.name || source.code).trim(),
          }));

        const createdSourceOptions = buildCreateFormLeadSourceOptions({
          groupingSources: Array.from(uniqueSourceMap.values()).map(
            (option) => ({
              id: option.value,
              label: option.label,
            })
          ),
          masterSources,
        }).map((option) => ({
          value: option.id,
          label: option.label,
        }));

        setSourceOptionsFromApi(
          createdSourceOptions.length > 0 ? createdSourceOptions : sourceOptions
        );
      } catch {
        if (cancelled) return;
        setSourceOptionsFromApi([]);
      }
    };

    void loadGroupings();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const unitOptions = facilities.map(
    (u: { id: string; name: string; displayName?: string }) => ({
      label: (u.displayName ?? u.name ?? "").trim(),
      value: u.id,
    })
  );

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setIsDrawerVisible(true));
      setSubmitError(null);
    } else {
      setIsDrawerVisible(false);
    }
  }, [open]);

  const validationSchema = isPatientEditMode
    ? editPatientCrmValidationSchema
    : addNewContactValidationSchema;

  const formik = useFormik<FormValues>({
    initialValues: mergedInitialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, helpers) => {
      setSubmitError(null);

      if (isEditMode) {
        if (!saveEnabled) {
          return;
        }

        const baseline = initialSnapshotRef.current ?? mergedInitialValues;

        if (isPatientEditMode) {
          if (!patientId) {
            return;
          }
          const patchPayload = buildPatientCrmPatchPayloadFromFormDiff(
            baseline,
            values,
            {
              displaySameAsPhone: sameAsPhone,
              whatsappSameAsPhone: whatsappSameAsPhone,
            }
          );
          if (Object.keys(patchPayload).length === 0) {
            toast.info("No changes to save.");
            return;
          }
          try {
            await patchPatientApi(patientId, patchPayload);
            toast.success("Patient updated successfully.");
            onContactUpdated?.();
            close();
          } catch (err: unknown) {
            let message = "Failed to update patient.";
            if (err instanceof AxiosError) {
              const data = err.response?.data;
              if (data && typeof data === "object" && "message" in data) {
                message = String((data as { message?: unknown }).message);
              }
            }
            setSubmitError(message);
            toast.error(message);
          }
          return;
        }

        if (!contactId) {
          return;
        }
        const patchPayload = buildPatchPayloadFromFormDiff(baseline, values);
        if (Object.keys(patchPayload).length === 0) {
          const clearedGender =
            Boolean(formGenderToPatch(baseline.gender)) &&
            !formGenderToPatch(values.gender);
          toast.info(
            clearedGender
              ? "Gender cannot be removed from the contact. Choose a gender or leave the previous value."
              : "No changes to save."
          );
          return;
        }
        try {
          await patchContactApi(contactId, patchPayload);
          toast.success("Contact updated successfully.");
          onContactUpdated?.();
          close();
        } catch (err: unknown) {
          let message = "Failed to update contact.";
          if (err instanceof AxiosError) {
            const data = err.response?.data;
            if (data && typeof data === "object" && "message" in data) {
              message = String((data as { message?: unknown }).message);
            }
          }
          setSubmitError(message);
          toast.error(message);
        }
        return;
      }

      const dob = getDobForApi(values.dob || "", values.age || "");
      const contactNo = toPhoneDigits(values.phoneNumber || "").slice(-12);
      const whatsappDigits = toPhoneDigits(values.whatsappNumber || "");
      const sourceCatalog = (
        sourceOptionsFromApi.length > 0 ? sourceOptionsFromApi : sourceOptions
      ).map((option) => ({ id: option.value, label: option.label }));
      const resolvedSource =
        resolveLeadSourceForApi(values.source ?? "", sourceCatalog) || "Manual";
      if (
        sourceRequiresChannel(values.source ?? "") &&
        !values.channel?.trim()
      ) {
        void helpers.setFieldTouched("channel", true, false);
        setSubmitError("Channel is required");
        return;
      }
      const genderCode = formGenderToHisCode(values.gender);
      const address = buildOptionalUpsertAddress(values);
      const payload: CreatePatientPayload = {
        Patient_First_Name: values.patientName?.trim() || "",
        Middle_Name: "",
        last_name: "",
        Contact_no: contactNo || "",
        ...(whatsappDigits ? { WhatsApp_number: whatsappDigits } : {}),
        ...(genderCode ? { gender: genderCode } : {}),
        Email_id: values.email?.trim() || "",
        DOB: dob || undefined,
        primaryFacilityId: values.unit?.trim() || undefined,
        utmSource: resolvedSource,
        leadSource: resolvedSource,
        ...(sourceShowsChannelField(values.source ?? "") &&
        values.channel?.trim()
          ? { sourceChannel: values.channel.trim() }
          : {}),
        preferredUnit: values.preferredUnit?.trim() || "",
        payerType: values.payerType?.trim() || "",
        opportunityType: values.opportunity?.trim() || "",
        ...(address ? { address } : {}),
      };
      try {
        const response = await createPatientApi(payload);
        if (response.success && response.data?.contactId) {
          const contactId = response.data.contactId;
          completedRef.current = true;
          try {
            flowRef.current?.complete(
              "lead_created",
              buildLeadCreatedProps(values)
            );
          } catch {
            // fail-open: analytics must never block lead creation
          }
          formik.resetForm();
          setSameAsPhone(false);
          setManualAddressEntry(false);
          setManualPincodeOptions([]);
          userEditedPincodeRef.current = false;
          close();
          navigate(`/leadDetailsPage/contact/${contactId}`, {
            state: { returnTo: "my-leads" },
          });
        } else {
          setSubmitError("Failed to create patient");
        }
      } catch (err: unknown) {
        let message = "Failed to create patient";
        if (err instanceof AxiosError) {
          const data = err.response?.data;
          if (data && typeof data === "object") {
            message = getApiValidationErrorMessage(data, message);
          } else if (err.message) {
            message = err.message;
          }
        }
        setSubmitError(message);
      }
    },
  });

  const { values, setFieldValue } = formik;

  // Analytics: lead_create funnel — start on open (create mode only), reset guards.
  useEffect(() => {
    if (!open || isEditMode) return;
    detailsEnteredRef.current = false;
    completedRef.current = false;
    flowBaselineRef.current = null;
    try {
      flowRef.current = startFlow("lead_create");
      flowRef.current.step("lead_create_started", {});
    } catch {
      flowRef.current = null;
    }
  }, [open, isEditMode]);

  // Analytics: fire lead_create_details_entered once, on the first genuine field
  // edit. The baseline is captured per open (first settle after the modal opens)
  // so a reopened modal with a stale form — or a programmatic phone prefill —
  // does not false-fire and skew the US-D2 funnel.
  useEffect(() => {
    if (!open || isEditMode || detailsEnteredRef.current) return;
    if (flowBaselineRef.current === null) {
      flowBaselineRef.current = values;
      return;
    }
    if (!hasEnteredContactDetails(values, flowBaselineRef.current)) return;
    detailsEnteredRef.current = true;
    try {
      flowRef.current?.step("lead_create_details_entered", {});
    } catch {
      // fail-open: analytics must never block data entry
    }
  }, [open, isEditMode, values]);

  // Analytics: a route-level unmount is an exit too. The modal is
  // always-mounted and self-gates on `open`, so the explicit UI exits run
  // their own abandon — but browser-back / route changes unmount the HOST
  // page and skip them, silently leaking the US-D2 funnel (holistic-review
  // MINOR). completedRef suppresses abandonment after lead_created; flow.ts
  // abandon() is idempotent besides.
  useEffect(() => {
    return () => {
      if (flowRef.current && !completedRef.current) {
        try {
          flowRef.current.abandon();
        } catch {
          // fail-open: analytics must never block navigation
        }
      }
    };
  }, []);

  // Snapshot initial values when edit drawer opens (for PATCH diff)
  useEffect(() => {
    if (open && isEditMode && !prevOpenRef.current) {
      initialSnapshotRef.current = { ...mergedInitialValues };
    }
    if (!open) {
      initialSnapshotRef.current = null;
    }
    prevOpenRef.current = open;
  }, [open, isEditMode, mergedInitialValues]);

  // CAR-2845: Source dropdown matches by exact option value. After options load,
  // map API/prefill values (e.g. VOICE_OC) onto the catalog option id (e.g.
  // "Voice OC" from campaign groupings) so the label renders instead of blank.
  useEffect(() => {
    if (!open || !isEditMode) return;
    const catalog = (
      sourceOptionsFromApi.length > 0 ? sourceOptionsFromApi : sourceOptions
    ).map((option) => ({ id: option.value, label: option.label }));
    if (catalog.length === 0) return;

    const current = (values.source ?? "").trim();
    if (!current) return;
    if (catalog.some((opt) => opt.id === current)) return;

    const catalogId = matchLeadSourceCatalogOptionId(current, catalog);
    if (!catalogId || catalogId === current) return;

    void setFieldValue("source", catalogId, false);
    if (initialSnapshotRef.current) {
      initialSnapshotRef.current = {
        ...initialSnapshotRef.current,
        source: catalogId,
      };
    }
  }, [open, isEditMode, sourceOptionsFromApi, values.source, setFieldValue]);

  // When opened with initial phone (e.g. from lead details New Contact), pre-fill and lock phone, display, and WhatsApp
  useEffect(() => {
    if (!isEditMode && open && initialPhoneNumber?.trim()) {
      const phone = initialPhoneNumber.trim();
      setFieldValue("phoneNumber", phone);
      setFieldValue("displayNumber", phone);
      setFieldValue("whatsappNumber", phone);
      setSameAsPhone(true);
      setWhatsappSameAsPhone(true);
    }
  }, [open, initialPhoneNumber, setFieldValue, isEditMode]);

  // CareFlow search prefill (CAR-2831): sync "same as phone" toggles; fields stay editable
  useEffect(() => {
    if (!isEditMode && open && searchPrefill?.phoneNumber?.trim()) {
      setSameAsPhone(true);
      setWhatsappSameAsPhone(true);
    }
  }, [open, searchPrefill, isEditMode]);

  useEffect(() => {
    if (open && isEditMode && prefillValues) {
      const phone = prefillValues.phoneNumber ?? "";
      setSameAsPhone(
        isPhoneSameAsRegistered(prefillValues.displayNumber, phone)
      );
      setWhatsappSameAsPhone(
        isPhoneSameAsRegistered(prefillValues.whatsappNumber, phone)
      );
    }
  }, [open, isEditMode, prefillValues]);

  // Sync display number when same as phone
  const handleSameAsPhoneChange = useCallback(
    (checked: boolean) => {
      setSameAsPhone(checked);
      if (checked) {
        setFieldValue("displayNumber", values.phoneNumber);
      } else {
        setFieldValue("displayNumber", "");
      }
    },
    [values.phoneNumber, setFieldValue]
  );

  const handleWhatsappSameAsPhoneChange = useCallback(
    (checked: boolean) => {
      setWhatsappSameAsPhone(checked);
      if (checked) {
        setFieldValue("whatsappNumber", values.phoneNumber);
      } else {
        setFieldValue("whatsappNumber", "");
      }
    },
    [values.phoneNumber, setFieldValue]
  );

  // Sync display number when phone changes and sameAsPhone is checked
  useEffect(() => {
    if (sameAsPhone && values.phoneNumber) {
      setFieldValue("displayNumber", values.phoneNumber);
    }
  }, [values.phoneNumber, sameAsPhone, setFieldValue]);

  // Sync WhatsApp number when phone changes and whatsappSameAsPhone is checked
  useEffect(() => {
    if (whatsappSameAsPhone && values.phoneNumber) {
      setFieldValue("whatsappNumber", values.phoneNumber);
    }
  }, [values.phoneNumber, whatsappSameAsPhone, setFieldValue]);

  const autoFilledManualCountryRef = useRef(false);

  const handleManualAddressEntryChange = useCallback(
    (checked: boolean) => {
      setManualAddressEntry(checked);
      setAddressResults([]);
      setAddressSearchError(null);
      setIsSearchAddressFocused(false);
      userEditedPincodeRef.current = false;
      if (!checked) {
        setManualPincodeOptions([]);
        const hasEnteredAddress = Boolean(
          values.searchAddress?.trim() ||
          values.city?.trim() ||
          values.state?.trim() ||
          values.pincode?.trim()
        );
        if (autoFilledManualCountryRef.current && !hasEnteredAddress) {
          setFieldValue("country", "");
        }
        autoFilledManualCountryRef.current = false;
        return;
      }
      if (!values.country?.trim()) {
        setFieldValue("country", "India");
        autoFilledManualCountryRef.current = true;
      }
    },
    [
      setFieldValue,
      values.country,
      values.searchAddress,
      values.city,
      values.state,
      values.pincode,
    ]
  );

  useEffect(() => {
    if (!manualAddressEntry) return;
    let cancelled = false;
    setIndianAddressLoading(true);
    loadIndianAddressData()
      .then((data) => {
        if (!cancelled) {
          setIndianAddressData(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIndianAddressData(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIndianAddressLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [manualAddressEntry]);

  const manualStateOptions = useMemo((): SingleSelectOption[] => {
    if (!indianAddressData) return [];
    return toLocationSelectOptions(indianAddressData.states);
  }, [indianAddressData]);

  const manualCityOptions = useMemo((): SingleSelectOption[] => {
    if (!indianAddressData || !values.state?.trim()) return [];
    return toLocationSelectOptions(
      getDistrictsForState(indianAddressData, values.state)
    );
  }, [indianAddressData, values.state]);

  useEffect(() => {
    if (!manualAddressEntry || !indianAddressData) {
      setManualPincodeOptions([]);
      return;
    }
    const state = values.state?.trim();
    const city = values.city?.trim();
    if (!state || !city) {
      setManualPincodeOptions([]);
      return;
    }
    setManualPincodeOptions(
      toLocationSelectOptions(
        getPincodesForStateCity(indianAddressData, state, city)
      )
    );
  }, [manualAddressEntry, indianAddressData, values.state, values.city]);

  useEffect(() => {
    if (
      !manualAddressEntry ||
      userEditedPincodeRef.current ||
      values.pincode?.trim() ||
      manualPincodeOptions.length === 0
    ) {
      return;
    }
    setFieldValue("pincode", manualPincodeOptions[0].value);
  }, [manualAddressEntry, manualPincodeOptions, values.pincode, setFieldValue]);

  const handleManualStateChange = useCallback(
    (stateName: string) => {
      userEditedPincodeRef.current = false;
      setFieldValue("state", stateName);
      setFieldValue("city", "");
      setFieldValue("pincode", "");
      setManualPincodeOptions([]);
      setTimeout(() => formik.setFieldTouched("state", true), 0);
    },
    [setFieldValue, formik]
  );

  const handleManualCityChange = useCallback(
    (cityName: string) => {
      const stateName = values.state?.trim() ?? "";
      setFieldValue("city", cityName);
      if (!values.country?.trim()) {
        setFieldValue("country", "India");
      }
      const pins =
        indianAddressData && stateName
          ? getPincodesForStateCity(indianAddressData, stateName, cityName)
          : [];
      setManualPincodeOptions(toLocationSelectOptions(pins));
      if (!userEditedPincodeRef.current) {
        setFieldValue("pincode", pins[0] ?? "");
      }
      setTimeout(() => formik.setFieldTouched("city", true), 0);
    },
    [setFieldValue, formik, indianAddressData, values.state, values.country]
  );

  const handleManualPincodeChange = useCallback(
    (pin: string) => {
      userEditedPincodeRef.current = true;
      setFieldValue("pincode", pin);
      setTimeout(() => formik.setFieldTouched("pincode", true), 0);
    },
    [setFieldValue, formik]
  );

  // Pincode → address, city, state, country (India Post). Skip when pincode was just set by address→pincode.
  const handlePincodeBlur = useCallback(() => {
    formik.handleBlur({
      target: { name: "pincode" },
    } as React.FocusEvent<HTMLInputElement>);
    if (manualAddressEntry) return;
    if (skipPincodeToAddressRef.current) return;
    const pin = (values.pincode || "").trim();
    if (!/^\d{6}$/.test(pin)) return;
    fetchLocationByPincode(pin).then((loc) => {
      if (!loc) return;
      setFieldValue("searchAddress", loc.address);
      setFieldValue("city", loc.city);
      setFieldValue("state", loc.state);
      setFieldValue("country", loc.country);
      skipAddressToPincodeRef.current = true;
      setTimeout(() => {
        skipAddressToPincodeRef.current = false;
      }, SKIP_AUTOFILL_MS);
    });
  }, [values.pincode, setFieldValue, formik, manualAddressEntry]);

  // Address search → suggestions (backend /address/search). Debounced.
  useEffect(() => {
    if (manualAddressEntry) {
      setAddressResults([]);
      setAddressSearchError(null);
      return;
    }
    const query = (values.searchAddress || "").trim();
    if (!open) {
      setAddressResults([]);
      setAddressSearchError(null);
      return;
    }
    if (!query || query.length < 2) {
      setAddressResults([]);
      setAddressSearchError(null);
      return;
    }
    if (!isSearchAddressFocused) {
      return;
    }
    if (
      addressSearchDisabledUntil != null &&
      Date.now() < addressSearchDisabledUntil
    ) {
      return;
    }

    if (addressToPincodeTimerRef.current) {
      clearTimeout(addressToPincodeTimerRef.current);
    }

    addressToPincodeTimerRef.current = setTimeout(() => {
      addressToPincodeTimerRef.current = null;
      (async () => {
        setIsAddressSearching(true);
        setAddressSearchError(null);
        try {
          const res = await apiClient.get<{
            success?: boolean;
            data?: AddressSearchResult[];
          }>("/address/search", {
            params: { q: query, limit: 8 },
          });
          const payload = res.data;
          if (payload && payload.success && Array.isArray(payload.data)) {
            setAddressResults(payload.data);
          } else {
            setAddressResults([]);
          }
        } catch (error: unknown) {
          setAddressResults([]);
          if (error instanceof AxiosError) {
            const status = error.response?.status;
            if (status === 429) {
              const headers = error.response?.headers ?? {};
              const retryAfterHeader =
                (headers["retry-after"] as string | undefined) ??
                (headers["Retry-After"] as string | undefined);
              const retryAfterSeconds = Number(retryAfterHeader) || 30;
              const disabledUntilTs = Date.now() + retryAfterSeconds * 1000;
              setAddressSearchDisabledUntil(disabledUntilTs);
              setAddressSearchError(
                `Too many address searches. Please wait ${retryAfterSeconds} seconds and try again.`
              );
            } else {
              const data = error.response?.data as
                { message?: string; error?: string } | undefined;
              const message =
                (data?.message && String(data.message)) ||
                (data?.error && String(data.error)) ||
                "Address search failed. Please try again.";
              setAddressSearchError(message);
            }
          } else {
            setAddressSearchError("Address search failed. Please try again.");
          }
        } finally {
          setIsAddressSearching(false);
        }
      })();
    }, ADDRESS_TO_PINCODE_DEBOUNCE_MS);

    return () => {
      if (addressToPincodeTimerRef.current) {
        clearTimeout(addressToPincodeTimerRef.current);
        addressToPincodeTimerRef.current = null;
      }
    };
  }, [
    values.searchAddress,
    open,
    isSearchAddressFocused,
    addressSearchDisabledUntil,
    manualAddressEntry,
  ]);

  const handleAddressSuggestionClick = useCallback(
    (result: AddressSearchResult) => {
      const addressText = result.placeName || result.placeAddress || "";
      setFieldValue("searchAddress", addressText);
      if (result.city) setFieldValue("city", result.city);
      if (result.state) setFieldValue("state", result.state);
      if (result.country) setFieldValue("country", result.country);
      if (result.pincode) setFieldValue("pincode", result.pincode);
      setAddressResults([]);
      setAddressSearchError(null);
      setIsSearchAddressFocused(false);
    },
    [setFieldValue]
  );

  // Analytics: single exit handler — abandon the funnel if not yet completed.
  const handleFlowClose = useCallback(() => {
    if (!isEditMode && !completedRef.current) {
      try {
        flowRef.current?.abandon();
        flowRef.current = null;
      } catch {
        // fail-open: analytics must never block closing the modal
      }
    }
    close();
  }, [close, isEditMode]);

  const handleCancelClick = () => {
    formik.resetForm();
    setSameAsPhone(false);
    setManualAddressEntry(false);
    setManualPincodeOptions([]);
    userEditedPincodeRef.current = false;
    setSubmitError(null);
    handleFlowClose();
  };

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        handleFlowClose();
      }
    },
    [handleFlowClose]
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    formik.handleSubmit(e);
  };

  if (!open) return null;

  const isAddressSearchRateLimited =
    addressSearchDisabledUntil != null &&
    Date.now() < addressSearchDisabledUntil;

  return (
    <div
      id="add-new-contact-modal-page"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "flex-end",
      }}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-new-contact-modal-title"
    >
      {/* Backdrop */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          opacity: isDrawerVisible ? 1 : 0,
          transition: `opacity ${DRAWER_SLIDE_DURATION_MS}ms ease`,
        }}
        aria-hidden="true"
        onClick={handleFlowClose}
      />

      {/* Drawer */}
      <form
        onSubmit={handleSubmit}
        style={{
          position: "relative",
          zIndex: 51,
          width: DRAWER_WIDTH,
          maxWidth: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transform: isDrawerVisible ? "translateX(0)" : "translateX(100%)",
          transition: `transform ${DRAWER_SLIDE_DURATION_MS}ms ease`,
          boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.12)",
        }}
      >
        <div
          id="add-new-contact-modal-container"
          tabIndex={-1}
          style={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            borderTopLeftRadius: getSpacing("16px"),
            borderBottomLeftRadius: getSpacing("16px"),
            backgroundColor: colorTokens["Background/White"],
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div
            id="add-new-contact-modal-header"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: `${getSpacing("16px")} ${getSpacing("24px")}`,
              borderBottom: `1px solid ${colorTokens["Borders/Light"]}`,
            }}
          >
            <h2
              id="add-new-contact-modal-title"
              style={{
                fontFamily: typographyTokens["Headings/H3"].family,
                fontSize: `${typographyTokens["Headings/H3"].size}px`,
                fontWeight: typographyTokens["Headings/H3"].weight,
                lineHeight: typographyTokens["Headings/H3"].lineHeight,
                color: colorTokens["Text/Black"],
                margin: 0,
              }}
            >
              {isPatientEditMode
                ? "Edit Patient"
                : isEditMode
                  ? "Edit Contact"
                  : "Add New Contact"}
            </h2>
            <button
              id="add-new-contact-modal-close-button"
              type="button"
              onClick={handleFlowClose}
              aria-label="Close modal"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: getSpacing("4px"),
                color: colorTokens["Text/Light Grey"],
                fontSize: `${typographyTokens["Headings/H3"].size}px`,
                lineHeight: 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = colorTokens["Text/Black"];
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = colorTokens["Text/Light Grey"];
              }}
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div
            className="no-scrollbar"
            id="add-new-contact-modal-body"
            style={{
              flex: 1,
              overflowY: "auto",
              padding: `${getSpacing("24px")} ${getSpacing("24px")}`,
            }}
          >
            {/* Contact Details */}
            <section
              id="add-new-contact-contact-details-section"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: getSpacing("16px"),
                marginBottom: getSpacing("32px"),
              }}
            >
              <h3
                style={{
                  ...getTypography("Headings/H3"),
                  color: colorTokens["Text/Black"],
                  margin: 0,
                  marginBottom: getSpacing("16px"),
                }}
              >
                Contact Details
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: getSpacing("12px"),
                }}
              >
                {/* Patient Name */}
                <div>
                  <TextField
                    id="add-new-contact-patient-name-input"
                    name="patientName"
                    label="Patient Name *"
                    placeholder="Patient Name *"
                    value={values.patientName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.patientName &&
                      Boolean(formik.errors.patientName)
                    }
                    helperText={
                      formik.touched.patientName && formik.errors.patientName
                        ? formik.errors.patientName
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                    disabled={formFieldsDisabled}
                    sx={textFieldSx}
                  />
                </div>

                {/* Gender */}
                <div>
                  <SingleSelectDropdown
                    idPrefix="add-new-contact-gender"
                    id="add-new-contact-gender-select"
                    label="Gender"
                    placeholder="Select Gender"
                    options={genderOptions as SingleSelectOption[]}
                    disabled={formFieldsDisabled}
                    value={values.gender}
                    onChange={(v) => {
                      formik.setFieldValue("gender", v);
                      setTimeout(
                        () => formik.setFieldTouched("gender", true),
                        0
                      );
                    }}
                    clearable
                    error={
                      formik.touched.gender && Boolean(formik.errors.gender)
                    }
                    helperText={
                      formik.touched.gender && formik.errors.gender
                        ? formik.errors.gender
                        : ""
                    }
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <TextField
                    id="add-new-contact-phone-number-input"
                    name="phoneNumber"
                    label="Phone Number *"
                    placeholder="Phone Number *"
                    type="tel"
                    value={values.phoneNumber}
                    onChange={(e) => {
                      if (phoneNumberLocked || formFieldsDisabled) return;
                      formik.handleChange(e);
                      if (sameAsPhone) {
                        setFieldValue("displayNumber", e.target.value);
                      }
                      if (whatsappSameAsPhone) {
                        setFieldValue("whatsappNumber", e.target.value);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.phoneNumber &&
                      Boolean(formik.errors.phoneNumber)
                    }
                    helperText={
                      formik.touched.phoneNumber && formik.errors.phoneNumber
                        ? formik.errors.phoneNumber
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                    disabled={formFieldsDisabled || phoneNumberLocked}
                    sx={{
                      ...textFieldSx,
                      ...(phoneNumberLocked || formFieldsDisabled
                        ? {
                            "& .MuiOutlinedInput-root": {
                              backgroundColor:
                                colorTokens["Background/Greyish"],
                              cursor: "not-allowed",
                              "& fieldset": {
                                borderColor: colorTokens["Borders/Light"],
                              },
                              "& input": {
                                cursor: "not-allowed",
                                WebkitTextFillColor: colorTokens["Text/Black"],
                                opacity: 1,
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: colorTokens["Text/Light Grey"],
                            },
                          }
                        : {}),
                    }}
                  />
                </div>

                {/* Email */}
                <div>
                  <TextField
                    id="add-new-contact-email-input"
                    name="email"
                    label="Email"
                    placeholder="Email"
                    type="email"
                    value={values.email || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={
                      formik.touched.email && formik.errors.email
                        ? formik.errors.email
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                    disabled={crmFieldsDisabled}
                    sx={textFieldSx}
                  />
                </div>

                {/* Display Number */}
                <div>
                  <TextField
                    id="add-new-contact-display-number-input"
                    name="displayNumber"
                    label="Display Number"
                    placeholder="Display Number"
                    type="tel"
                    value={values.displayNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.displayNumber &&
                      Boolean(formik.errors.displayNumber)
                    }
                    helperText={
                      formik.touched.displayNumber &&
                      formik.errors.displayNumber
                        ? formik.errors.displayNumber
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                    disabled={
                      crmFieldsDisabled || sameAsPhone || phoneNumberLocked
                    }
                    sx={{
                      ...textFieldSx,
                      ...(crmFieldsDisabled || sameAsPhone || phoneNumberLocked
                        ? sameAsPhoneDisabledFieldSx
                        : {}),
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: getSpacing("8px"),
                      margin: `${getSpacing("8px")}`,
                    }}
                  >
                    <FormControlLabel
                      disabled={crmFieldsDisabled || phoneNumberLocked}
                      control={
                        <Checkbox
                          id="add-new-contact-same-as-phone-checkbox"
                          checked={sameAsPhone}
                          onChange={(e) =>
                            handleSameAsPhoneChange(e.target.checked)
                          }
                          icon={
                            <img
                              src={checkboxIcon}
                              alt=""
                              aria-hidden="true"
                              style={{ width: "16px", height: "16px" }}
                            />
                          }
                          checkedIcon={
                            <img
                              src={checkboxCheckedIcon}
                              alt=""
                              aria-hidden="true"
                              style={{ width: "16px", height: "16px" }}
                            />
                          }
                          sx={{
                            padding: 0,
                            color: "transparent",
                            "&.Mui-checked": {
                              color: "transparent",
                            },
                          }}
                        />
                      }
                      label={
                        <span
                          style={{
                            marginLeft: `${getSpacing("8px")}`,

                            fontFamily: typographyTokens["Body/Medium"].family,
                            fontSize: `${typographyTokens["Body/Medium"].size}px`,
                            fontWeight: typographyTokens["Body/Medium"].weight,
                            lineHeight:
                              typographyTokens["Body/Medium"].lineHeight,
                            color: colorTokens["Text/Light Grey"],
                          }}
                        >
                          Same as phone number
                        </span>
                      }
                    />
                  </div>
                </div>

                {/* WhatsApp Number (sent as WhatsApp_number to API) */}
                <div>
                  <TextField
                    id="add-new-contact-whatsapp-number-input"
                    name="whatsappNumber"
                    label="WhatsApp Number"
                    placeholder="WhatsApp Number"
                    type="tel"
                    value={values.whatsappNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.whatsappNumber &&
                      Boolean(formik.errors.whatsappNumber)
                    }
                    helperText={
                      formik.touched.whatsappNumber &&
                      formik.errors.whatsappNumber
                        ? formik.errors.whatsappNumber
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                    disabled={
                      crmFieldsDisabled ||
                      whatsappSameAsPhone ||
                      phoneNumberLocked
                    }
                    sx={{
                      ...textFieldSx,
                      ...(crmFieldsDisabled ||
                      whatsappSameAsPhone ||
                      phoneNumberLocked
                        ? sameAsPhoneDisabledFieldSx
                        : {}),
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: getSpacing("8px"),
                      margin: `${getSpacing("8px")}`,
                    }}
                  >
                    <FormControlLabel
                      disabled={crmFieldsDisabled || phoneNumberLocked}
                      control={
                        <Checkbox
                          id="add-new-contact-whatsapp-same-as-phone-checkbox"
                          checked={whatsappSameAsPhone}
                          onChange={(e) =>
                            handleWhatsappSameAsPhoneChange(e.target.checked)
                          }
                          icon={
                            <img
                              src={checkboxIcon}
                              alt=""
                              aria-hidden="true"
                              style={{ width: "16px", height: "16px" }}
                            />
                          }
                          checkedIcon={
                            <img
                              src={checkboxCheckedIcon}
                              alt=""
                              aria-hidden="true"
                              style={{ width: "16px", height: "16px" }}
                            />
                          }
                          sx={{
                            padding: 0,
                            color: "transparent",
                            "&.Mui-checked": {
                              color: "transparent",
                            },
                          }}
                        />
                      }
                      label={
                        <span
                          style={{
                            fontFamily: typographyTokens["Body/Medium"].family,
                            fontSize: `${typographyTokens["Body/Medium"].size}px`,
                            fontWeight: typographyTokens["Body/Medium"].weight,
                            lineHeight:
                              typographyTokens["Body/Medium"].lineHeight,
                            color: colorTokens["Text/Light Grey"],
                            marginLeft: `${getSpacing("8px")}`,
                          }}
                        >
                          Same as phone number
                        </span>
                      }
                    />
                  </div>
                </div>

                {/* DOB */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                  }}
                >
                  <TextField
                    id="add-new-contact-dob-input"
                    name="dob"
                    label="DOB"
                    placeholder="DOB"
                    value={values.dob || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue("dob", value);
                      formik.setFieldValue("age", getAgeFromDob(value));
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.dob && Boolean(formik.errors.dob)}
                    helperText={
                      formik.touched.dob && formik.errors.dob
                        ? formik.errors.dob
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                    disabled={formFieldsDisabled}
                    sx={textFieldSx}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <img
                            src={CalenderIcon}
                            alt="Calendar"
                            style={{
                              width: getSpacing("16px"),
                              height: getSpacing("16px"),
                              cursor: formFieldsDisabled
                                ? "not-allowed"
                                : "pointer",
                            }}
                            onClick={() => {
                              if (formFieldsDisabled) return;
                              formik.setFieldTouched("dob", true, false);
                              setIsDobCalendarOpen((prev) => !prev);
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {isDobCalendarOpen && (
                    <DatePickerCalendar
                      isOpen={isDobCalendarOpen}
                      onClose={() => setIsDobCalendarOpen(false)}
                      onDateSelect={(date) => {
                        formik.setFieldValue("dob", date);
                        formik.setFieldValue("age", getAgeFromDob(date));
                        setIsDobCalendarOpen(false);
                      }}
                      selectedDate={values.dob || ""}
                      idPrefix="add-new-contact-dob"
                      allowPastDates
                      disableFutureDates
                    />
                  )}
                </div>

                {/* Age with OR */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: getSpacing("12px"),
                  }}
                >
                  <span
                    style={{
                      fontFamily: typographyTokens["Body/Medium"].family,
                      fontSize: `${typographyTokens["Body/Medium"].size}px`,
                      fontWeight: typographyTokens["Body/Medium"].weight,
                      color: colorTokens["Text/Light Grey"],
                      whiteSpace: "nowrap",
                    }}
                  >
                    OR
                  </span>
                  <TextField
                    id="add-new-contact-age-input"
                    name="age"
                    label="Age"
                    placeholder="Age"
                    type="number"
                    value={values.age || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      const ageVal = v === "" ? "" : String(v);
                      formik.setFieldValue("age", ageVal);
                      formik.setFieldValue("dob", getDobFromAge(ageVal));
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.age && Boolean(formik.errors.age)}
                    helperText={
                      formik.touched.age && formik.errors.age
                        ? formik.errors.age
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                    disabled={formFieldsDisabled}
                    sx={{ ...textFieldSx, flex: 1 }}
                  />
                </div>

                {/* Address */}
                <div
                  style={{
                    gridColumn: "1 / -1",
                    position: "relative",
                  }}
                >
                  <TextField
                    id="add-new-contact-search-address-input"
                    name="searchAddress"
                    label={
                      manualAddressEntry ? "Enter Address" : "Search Address"
                    }
                    placeholder={
                      manualAddressEntry ? "Enter Address" : "Search Address"
                    }
                    value={values.searchAddress}
                    onChange={(e) => {
                      if (!manualAddressEntry) {
                        setIsSearchAddressFocused(true);
                      }
                      formik.handleChange(e);
                    }}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      if (!manualAddressEntry) {
                        setIsSearchAddressFocused(false);
                      }
                    }}
                    onFocus={() => {
                      if (!manualAddressEntry) {
                        setIsSearchAddressFocused(true);
                      }
                    }}
                    error={
                      formik.touched.searchAddress &&
                      Boolean(formik.errors.searchAddress)
                    }
                    helperText={
                      formik.touched.searchAddress &&
                      formik.errors.searchAddress
                        ? formik.errors.searchAddress
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                    sx={textFieldSx}
                    disabled={
                      formFieldsDisabled ||
                      (!manualAddressEntry && isAddressSearchRateLimited)
                    }
                    InputProps={
                      manualAddressEntry
                        ? undefined
                        : {
                            endAdornment: (
                              <InputAdornment position="end">
                                <img
                                  src={SearchIcon}
                                  alt="Search"
                                  style={{
                                    width: getSpacing("16px"),
                                    height: getSpacing("16px"),
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }
                    }
                  />

                  {!manualAddressEntry &&
                    isSearchAddressFocused &&
                    addressResults.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          marginTop: getSpacing("4px"),
                          maxHeight: getSpacing("160px"),
                          overflowY: "auto",
                          backgroundColor: colorTokens["Background/White"],
                          border: `1px solid ${colorTokens["Borders/Light"]}`,
                          borderRadius: getSpacing("8px"),
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                          zIndex: 10,
                        }}
                      >
                        {addressResults.map((result) => (
                          <button
                            key={result.placeId}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                            }}
                            onClick={() => handleAddressSuggestionClick(result)}
                            style={{
                              display: "block",
                              width: "100%",
                              textAlign: "left",
                              padding: `${getSpacing("8px")} ${getSpacing(
                                "12px"
                              )}`,
                              border: "none",
                              backgroundColor: "transparent",
                              cursor: "pointer",
                            }}
                          >
                            <span
                              style={{
                                ...getTypography("Body/Medium"),
                                color: colorTokens["Text/Black"],
                              }}
                            >
                              {result.placeName}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                  {!manualAddressEntry && addressSearchError && (
                    <p
                      style={{
                        margin: `${getSpacing("4px")} 0 0`,
                        ...getTypography("Body/Small"),
                        color: colorTokens["Alert/Red/Dark"],
                      }}
                    >
                      {addressSearchError}
                    </p>
                  )}
                </div>

                {manualAddressEntry ? (
                  <>
                    {/* Country */}
                    <div>
                      <SingleSelectDropdown
                        idPrefix="add-new-contact-country"
                        id="add-new-contact-country-select"
                        label="Country"
                        placeholder="Country"
                        options={countryOptions as SingleSelectOption[]}
                        value={values.country || ""}
                        onChange={(v) => {
                          formik.setFieldValue("country", v);
                          setTimeout(
                            () => formik.setFieldTouched("country", true),
                            0
                          );
                        }}
                        disabled={formFieldsDisabled}
                      />
                    </div>

                    {/* State */}
                    <div>
                      <SingleSelectDropdown
                        idPrefix="add-new-contact-state"
                        id="add-new-contact-state-select"
                        label="State"
                        placeholder="Select State"
                        options={manualStateOptions}
                        value={values.state || ""}
                        onChange={handleManualStateChange}
                        ariaBusy={indianAddressLoading}
                        disabled={formFieldsDisabled || indianAddressLoading}
                        error={
                          formik.touched.state &&
                          Boolean(formik.errors.state) &&
                          !values.state
                        }
                        helperText={
                          formik.touched.state &&
                          !values.state &&
                          formik.errors.state
                            ? formik.errors.state
                            : ""
                        }
                      />
                    </div>

                    {/* City */}
                    <div>
                      <SingleSelectDropdown
                        idPrefix="add-new-contact-city"
                        id="add-new-contact-city-select"
                        label="City"
                        placeholder="Select City"
                        options={manualCityOptions}
                        value={values.city || ""}
                        onChange={handleManualCityChange}
                        disabled={formFieldsDisabled || !values.state?.trim()}
                        error={
                          formik.touched.city &&
                          Boolean(formik.errors.city) &&
                          !values.city
                        }
                        helperText={
                          formik.touched.city &&
                          !values.city &&
                          formik.errors.city
                            ? formik.errors.city
                            : ""
                        }
                      />
                    </div>

                    {/* Pincode */}
                    <div>
                      <SingleSelectDropdown
                        idPrefix="add-new-contact-pincode"
                        id="add-new-contact-pincode-select"
                        label="Pincode"
                        placeholder="Select Pincode"
                        options={manualPincodeOptions}
                        value={values.pincode || ""}
                        onChange={handleManualPincodeChange}
                        disabled={formFieldsDisabled || !values.city?.trim()}
                        error={
                          formik.touched.pincode &&
                          Boolean(formik.errors.pincode) &&
                          !values.pincode
                        }
                        helperText={
                          formik.touched.pincode && formik.errors.pincode
                            ? formik.errors.pincode
                            : ""
                        }
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* City */}
                    <div>
                      <TextField
                        id="add-new-contact-city-input"
                        name="city"
                        label="City"
                        placeholder="City"
                        value={values.city || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.city && Boolean(formik.errors.city)
                        }
                        helperText={
                          formik.touched.city && formik.errors.city
                            ? formik.errors.city
                            : ""
                        }
                        variant="outlined"
                        fullWidth
                        disabled
                        sx={textFieldSx}
                      />
                    </div>

                    {/* State */}
                    <div>
                      <TextField
                        id="add-new-contact-state-input"
                        name="state"
                        label="State"
                        placeholder="State"
                        value={values.state || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.state && Boolean(formik.errors.state)
                        }
                        helperText={
                          formik.touched.state && formik.errors.state
                            ? formik.errors.state
                            : ""
                        }
                        variant="outlined"
                        fullWidth
                        disabled
                        sx={textFieldSx}
                      />
                    </div>

                    {/* Country */}
                    <div>
                      <SingleSelectDropdown
                        idPrefix="add-new-contact-country"
                        id="add-new-contact-country-select"
                        label="Country"
                        placeholder="Country"
                        options={countryOptions as SingleSelectOption[]}
                        value={values.country || ""}
                        onChange={(v) => {
                          formik.setFieldValue("country", v);
                          setTimeout(
                            () => formik.setFieldTouched("country", true),
                            0
                          );
                        }}
                        disabled
                      />
                    </div>

                    {/* Pincode */}
                    <div>
                      <TextField
                        id="add-new-contact-pincode-input"
                        name="pincode"
                        label="Pincode"
                        placeholder="6-digit pincode"
                        value={values.pincode || ""}
                        onChange={formik.handleChange}
                        onBlur={handlePincodeBlur}
                        error={
                          formik.touched.pincode &&
                          Boolean(formik.errors.pincode)
                        }
                        helperText={
                          formik.touched.pincode && formik.errors.pincode
                            ? formik.errors.pincode
                            : ""
                        }
                        variant="outlined"
                        fullWidth
                        disabled={formFieldsDisabled}
                        sx={textFieldSx}
                      />
                    </div>
                  </>
                )}

                <div style={{ gridColumn: "1 / -1" }}>
                  <FormControlLabel
                    disabled={formFieldsDisabled}
                    control={
                      <Switch
                        id="add-new-contact-manual-address-toggle"
                        checked={manualAddressEntry}
                        onChange={(e) =>
                          handleManualAddressEntryChange(e.target.checked)
                        }
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: colorTokens["Primary/Default"],
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: colorTokens["Primary/Default"],
                            },
                        }}
                      />
                    }
                    label={
                      <span
                        style={{
                          fontFamily: typographyTokens["Body/Medium"].family,
                          fontSize: `${typographyTokens["Body/Medium"].size}px`,
                          fontWeight: typographyTokens["Body/Medium"].weight,
                          lineHeight:
                            typographyTokens["Body/Medium"].lineHeight,
                          color: colorTokens["Text/Light Grey"],
                        }}
                      >
                        Enter address manually
                      </span>
                    }
                  />
                </div>
              </div>
            </section>

            {/* Source & Unit */}
            <section
              id="add-new-contact-source-unit-section"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: getSpacing("8px"),
                marginBottom: getSpacing("32px"),
              }}
            >
              <h3
                style={{
                  ...getTypography("Headings/H3"),
                  color: colorTokens["Text/Black"],
                  margin: 0,
                  marginBottom: getSpacing("16px"),
                }}
              >
                Source & Unit
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: getSpacing("12px"),
                }}
              >
                {/* Unit - required */}
                <div>
                  <SingleSelectDropdown
                    idPrefix="add-new-contact-unit"
                    id="add-new-contact-unit-select"
                    label="Preferred Unit"
                    placeholder={
                      isPatientEditMode
                        ? "Select Preferred Unit"
                        : "Select Preferred Unit *"
                    }
                    options={unitOptions as SingleSelectOption[]}
                    value={values.unit || ""}
                    onChange={(v) => {
                      formik.setFieldValue("unit", v);
                      setTimeout(() => formik.setFieldTouched("unit", true), 0);
                    }}
                    required={!isPatientEditMode}
                    error={
                      !isPatientEditMode &&
                      formik.touched.unit &&
                      Boolean(formik.errors.unit) &&
                      !values.unit
                    }
                    helperText={
                      !isPatientEditMode &&
                      formik.touched.unit &&
                      !values.unit &&
                      formik.errors.unit
                        ? formik.errors.unit
                        : ""
                    }
                    disabled={crmFieldsDisabled}
                  />
                </div>

                {/* Source */}
                <div>
                  <SingleSelectDropdown
                    idPrefix="add-new-contact-source"
                    id="add-new-contact-source-select"
                    label="Source"
                    placeholder="Source"
                    options={
                      sourceOptionsFromApi.length > 0
                        ? sourceOptionsFromApi
                        : (sourceOptions as SingleSelectOption[])
                    }
                    value={values.source || ""}
                    onChange={(v) => {
                      formik.setFieldValue("source", v);
                      formik.setFieldValue(
                        "channel",
                        getAutoSelectedChannelForSource(v)
                      );
                      setTimeout(
                        () => formik.setFieldTouched("source", true),
                        0
                      );
                    }}
                    disabled={formFieldsDisabled}
                  />
                </div>

                {/* Channel — same visibility rules as Add Opportunity */}
                {sourceShowsChannelField(values.source ?? "") && (
                  <div id="add-new-contact-channel-container">
                    <SingleSelectDropdown
                      idPrefix="add-new-contact-channel"
                      id="add-new-contact-channel-select"
                      label="Channel"
                      placeholder="Channel"
                      placeholderSuffix={
                        sourceRequiresChannel(values.source ?? "") ? (
                          <span
                            style={{ color: colorTokens["Alert/Red/Dark"] }}
                          >
                            *
                          </span>
                        ) : undefined
                      }
                      options={getOpportunityChannelOptionsForSource(
                        values.source ?? ""
                      ).map((opt) => ({
                        value: opt.id,
                        label: opt.label,
                      }))}
                      value={values.channel || ""}
                      onChange={(v) => {
                        formik.setFieldValue("channel", v);
                        setTimeout(
                          () => formik.setFieldTouched("channel", true),
                          0
                        );
                      }}
                      required={sourceRequiresChannel(values.source ?? "")}
                      clearable={!sourceRequiresChannel(values.source ?? "")}
                      disabled={formFieldsDisabled}
                    />
                  </div>
                )}

                {/* Preferred Unit */}
                {/* <div>
                  <SingleSelectDropdown
                    idPrefix="add-new-contact-preferred-unit"
                    id="add-new-contact-preferred-unit-select"
                    label="Preferred Unit"
                    placeholder="Preferred Unit"
                    options={[]}
                    value={values.preferredUnit || ""}
                    onChange={(v) => {
                      formik.setFieldValue("preferredUnit", v);
                      setTimeout(() => formik.setFieldTouched("preferredUnit", true), 0);
                    }}
                  />
                </div> */}

                {/* Payer Type */}
                <div>
                  <SingleSelectDropdown
                    idPrefix="add-new-contact-payer-type"
                    id="add-new-contact-payer-type-select"
                    label="Payer Type"
                    placeholder="Payer Type"
                    options={payerTypeOptions as SingleSelectOption[]}
                    value={values.payerType || ""}
                    onChange={(v) => {
                      formik.setFieldValue("payerType", v);
                      setTimeout(
                        () => formik.setFieldTouched("payerType", true),
                        0
                      );
                    }}
                    disabled={formFieldsDisabled}
                  />
                </div>
              </div>
            </section>

            {/* Opportunity Details */}
            {/* <section
              id="add-new-contact-opportunity-details-section"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: getSpacing("16px"),
              }}
            >
              <h3
                style={{
                  ...getTypography("Headings/H3"),
                  color: colorTokens["Text/Black"],
                  margin: 0,
                  marginBottom: getSpacing("16px"),
                }}
              >
                Opportunity Details
              </h3>
              <div>
              <SingleSelectDropdown
                idPrefix="add-new-contact-opportunity"
                id="add-new-contact-opportunity-select"
                label="Opportunity"
                placeholder="Opportunity"
                options={opportunityOptions as SingleSelectOption[]}
                value={values.opportunity || ""}
                onChange={(v) => {
                  formik.setFieldValue("opportunity", v);
                  setTimeout(() => formik.setFieldTouched("opportunity", true), 0);
                }}
              />
              </div>
            </section> */}
          </div>

          {/* Footer */}
          <div
            id="add-new-contact-modal-footer"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: getSpacing("12px"),
              padding: `${getSpacing("16px")} ${getSpacing("24px")}`,
              borderTop: `1px solid ${colorTokens["Borders/Light"]}`,
              backgroundColor: colorTokens["Background/White"],
              position: "sticky",
              bottom: 0,
            }}
          >
            {submitError && (
              <div
                style={{
                  ...getTypography("Body/Small"),
                  color: colorTokens["Alert/Red/Dark"],
                }}
              >
                {submitError}
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: getSpacing("12px"),
              }}
            >
              <button
                id="add-new-contact-cancel-button"
                type="button"
                onClick={handleCancelClick}
                style={{
                  padding: `${getSpacing("8px")} ${getSpacing("20px")}`,
                  borderRadius: getSpacing("8px"),
                  border: `1px solid ${colorTokens["Primary/Default"]}`,
                  backgroundColor: "transparent",
                  fontFamily: typographyTokens["Links/Btn Text"].family,
                  fontSize: `${typographyTokens["Links/Btn Text"].size}px`,
                  fontWeight: typographyTokens["Links/Btn Text"].weight,
                  lineHeight: typographyTokens["Links/Btn Text"].lineHeight,
                  color: colorTokens["Primary/Default"],
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    colorTokens["Background/Bluish"];
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                Cancel
              </button>
              <button
                id="add-new-contact-next-button"
                type="submit"
                disabled={isReadOnly || !formik.isValid || formik.isSubmitting}
                style={{
                  padding: `${getSpacing("8px")} ${getSpacing("24px")}`,
                  borderRadius: getSpacing("8px"),
                  border: "none",
                  backgroundColor:
                    !isReadOnly && formik.isValid && !formik.isSubmitting
                      ? colorTokens["Primary/Default"]
                      : colorTokens["Borders/Light"],
                  fontFamily: typographyTokens["Links/Btn Text"].family,
                  fontSize: `${typographyTokens["Links/Btn Text"].size}px`,
                  fontWeight: typographyTokens["Links/Btn Text"].weight,
                  lineHeight: typographyTokens["Links/Btn Text"].lineHeight,
                  color:
                    !isReadOnly && formik.isValid && !formik.isSubmitting
                      ? colorTokens["Background/White"]
                      : colorTokens["Text/Light Grey"],
                  cursor:
                    !isReadOnly && formik.isValid && !formik.isSubmitting
                      ? "pointer"
                      : "not-allowed",
                }}
                aria-disabled={
                  isReadOnly || !formik.isValid || formik.isSubmitting
                }
              >
                {formik.isSubmitting
                  ? isEditMode
                    ? "Saving..."
                    : "Adding..."
                  : isEditMode
                    ? "Save"
                    : "Add Contact"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddNewContactModal;
